import {
  Form,
  Input,
  Table,
  Typography,
  Select,
  message,
  Button,
  Space,
  Layout,
  DatePicker,
  Popconfirm,
  Tag,
} from "antd";
import React, { useState, useEffect } from "react";
import httpUtil from "../../../../utils/httpUtil";
import dayjs from "dayjs";

const { Option } = Select;
const { Header } = Layout;

export const EvaluateRecords = ({userInfo}) => {
  
  useEffect(() => {
    getAllRecords();
  }, []);

  const [tableForm] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [editingKey, setEditingKey] = useState("");
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [searchParams, setSearchParams] = useState({
    record_id: "",
    student_id: "",
    indicator_id: "",
    evaluator_id: "",
    evaluation_date: "",
    state: "",
  });

  // 获取所有评价记录
  const getAllRecords = () => {
    setLoading(true);
    httpUtil
      .getAllEvaluationRecords()
      .then((res) => {
        if (res.success) {
          setData(res.data); // 直接使用返回的data数组
        } else {
          message.error(res.error);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // 搜索评价记录
  const handleSearch = () => {
    setLoading(true);

    // 创建干净的搜索参数对象，去除空值
    const cleanSearchParams = {};
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value !== "" && value !== undefined && value !== null) {
        cleanSearchParams[key] = value;
      }
    });

    // 如果没有提供任何搜索条件，获取所有记录
    if (Object.keys(cleanSearchParams).length === 0) {
      getAllRecords();
      return;
    }

    // 调用后端搜索API
    httpUtil
      .searchEvaluationRecords(cleanSearchParams)
      .then((res) => {
        if (res.success) {
          setData(res.data);
          message.success(`找到 ${res.data.length} 条记录`);
        } else {
          message.error(res.error || "搜索失败");
          // 保留当前数据不更新
        }
      })
      .catch((err) => {
        console.error("搜索评价记录错误:", err);
        message.error("搜索评价记录时出错");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // 重置搜索
  const handleReset = () => {
    setSearchParams({
      record_id: "",
      student_id: "",
      indicator_id: "",
      evaluator_id: "",
      evaluation_date: "",
      state: "",
    });
    getAllRecords();
  };

  // 表格编辑逻辑
  const isEditing = (record) => record.record_id === editingKey;

  const edit = (record) => {
    tableForm.setFieldsValue({
      ...record,
      evaluation_date: record.evaluation_date
        ? dayjs(record.evaluation_date, "YYYY-MM-DD")
        : null,
    });
    setEditingKey(record.record_id);
  };

  const cancel = () => {
    setEditingKey("");
  };

  const save = async (key) => {
    try {
      await tableForm.validateFields([
        "record_id",
        "student_id",
        "student_name",
        "indicator_id",
        "indicator_name",
        "score",
        "evaluation_date",
        "evaluator_id",
        "evaluator_name",
        "state",
      ]);

      const row = tableForm.getFieldsValue();

      if (
        row.evaluation_date &&
        typeof row.evaluation_date.format === "function"
      ) {
        row.evaluation_date = row.evaluation_date.format("YYYY-MM-DD");
      }

      // 确保score是数字类型
      if (row.score !== undefined) {
        row.score = Number(row.score);
      }

      row.comment = row.comment || null;

      const newData = [...data];
      const index = newData.findIndex((item) => item.record_id === key);

      if (index > -1) {
        const updatedRecord = {
          ...newData[index],
          ...row,
          student_name: row.student_name || newData[index].student_name,
          indicator_name: row.indicator_name || newData[index].indicator_name,
          evaluator_name: row.evaluator_name || newData[index].evaluator_name,
        };

        httpUtil.updateEvaluationRecord(updatedRecord).then((res) => {
          if (res.success) {
            newData.splice(index, 1, updatedRecord);
            setData(newData);
            setEditingKey("");
            message.success("评价记录更新成功");
          } else {
            message.error(res.error || "更新失败");
          }
        });
      }
    } catch (err) {
      console.log("保存评价记录错误:", err);
      message.error("保存评价记录时出错");
    }
  };

  // 删除记录
  const handleDelete = async (key) => {
    try {
      setLoading(true);
      const params = { record_id: key };
      httpUtil.deleteEvaluationRecord(params).then((res) => {
        if (res.success) {
          const newData = data.filter((item) => item.record_id !== key);
          setData(newData);
          message.success(`记录 ${key} 删除成功`);
          getAllRecords();
        } else {
          message.error(res.error || "删除记录失败");
        }
      });
    } catch (err) {
      console.error("删除记录错误:", err);
      message.error("删除记录时发生错误");
    } finally {
      setLoading(false);
    }
  };

  // 渲染状态标签
  const renderStateTag = (state) => {
    const colorMap = {
      已提交: "orange",
      已审核: "blue",
      已完成: "green",
      已驳回: "red",
      草稿: "gray",
      申诉中: "purple", // 新增申诉中状态
    };
    return <Tag color={colorMap[state]}>{state}</Tag>;
  };

  // 表格列配置
  const columns = [
    {
      title: "记录ID",
      dataIndex: "record_id",
      width: 100,
      editable: false,
      fixed: "left",
    },
    {
      title: "学号",
      dataIndex: "student_id",
      width: 100,
      editable: true,
    },
    {
      title: "学生姓名",
      dataIndex: "student_name",
      width: 100,
      editable: true,
    },
    {
      title: "指标ID",
      dataIndex: "indicator_id",
      width: 100,
      editable: true,
    },
    {
      title: "指标名称",
      dataIndex: "indicator_name",
      width: 120,
      editable: true,
    },
    {
      title: "评分",
      dataIndex: "score",
      width: 120,
      editable: true,
      render: (text) => {
        // 确保text是数字类型
        const num = Number(text);
        return isNaN(num) ? text : num.toFixed(2);
      },
    },
    {
      title: "评价日期",
      dataIndex: "evaluation_date",
      width: 150,
      editable: true,
    },
    {
      title: "评价人ID",
      dataIndex: "evaluator_id",
      width: 120,
      editable: true,
    },
    {
      title: "评价人姓名",
      dataIndex: "evaluator_name",
      width: 120,
      editable: true,
    },
    {
      title: "状态",
      dataIndex: "state",
      width: 120,
      editable: true,
      render: renderStateTag,
    },
    {
      title: "备注",
      dataIndex: "comment",
      width: 150,
      editable: true,
      render: (text) => text || "-",
    },
    {
      title: "操作",
      dataIndex: "operation",
      width: 120,
      fixed: "right",
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <Space>
            <Typography.Link onClick={() => save(record.record_id)}>
              保存
            </Typography.Link>
            <Typography.Link onClick={cancel}>取消</Typography.Link>
          </Space>
        ) : (
          <Space>
            <Typography.Link
              disabled={editingKey !== ""}
              onClick={() => edit(record)}
            >
              编辑
            </Typography.Link>
            <Popconfirm
              title="确定删除此记录吗?"
              onConfirm={() => handleDelete(record.record_id)}
              okText="确定"
              cancelText="取消"
            >
              <Typography.Link type="danger">删除</Typography.Link>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) return col;

    return {
      ...col,
      onCell: (record) => ({
        record,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        inputNode: (() => {
          if (col.dataIndex === "state") {
            return (
              <Select>
                <Option value="草稿">草稿</Option>
                <Option value="已提交">已提交</Option>
                <Option value="已审核">已审核</Option>
                <Option value="已驳回">已驳回</Option>
              </Select>
            );
          } else if (col.dataIndex === "evaluation_date") {
            return (
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                allowClear={false}
              />
            );
          } else if (col.dataIndex === "score") {
            return <Input type="number" step="0.01" />;
          }
          return <Input />;
        })(),
      }),
    };
  });

  const markEvaluationsAsCompleted =() => {
    const params={
      operator_id:userInfo.user_id
    }
    // console.log(params);
    httpUtil.markEvaluationsAsCompleted(params).then((res) => {
      if(res.success){
        message.success("已关闭公示！")
      }
    })
  }

  return (
    <Form form={tableForm} component={false}>
      <Header style={{ padding: 0, background: "#fff" }}>
        <Space align="center">
          <Input
            placeholder="记录ID"
            value={searchParams.record_id}
            onChange={(e) =>
              setSearchParams((prev) => ({
                ...prev,
                record_id: e.target.value,
              }))
            }
            style={{ width: 120 }}
          />
          <Input
            placeholder="学号"
            value={searchParams.student_id}
            onChange={(e) =>
              setSearchParams((prev) => ({
                ...prev,
                student_id: e.target.value,
              }))
            }
            style={{ width: 120 }}
          />
          <Input
            placeholder="指标ID"
            value={searchParams.indicator_id}
            onChange={(e) =>
              setSearchParams((prev) => ({
                ...prev,
                indicator_id: e.target.value,
              }))
            }
            style={{ width: 120 }}
          />
          <Input
            placeholder="评价人ID"
            value={searchParams.evaluator_id}
            onChange={(e) =>
              setSearchParams((prev) => ({
                ...prev,
                evaluator_id: e.target.value,
              }))
            }
            style={{ width: 120 }}
          />
          <DatePicker
            placeholder="评价日期"
            value={
              searchParams.evaluation_date
                ? dayjs(searchParams.evaluation_date)
                : null
            }
            onChange={(date, dateString) =>
              setSearchParams((prev) => ({
                ...prev,
                evaluation_date: dateString,
              }))
            }
            style={{ width: 150 }}
          />
          <Select
            placeholder="状态"
            value={searchParams.state || undefined}
            onChange={(value) =>
              setSearchParams((prev) => ({
                ...prev,
                state: value,
              }))
            }
            style={{ width: 120 }}
            allowClear
          >
            <Option value="草稿">草稿</Option>
            <Option value="已提交">已提交</Option>
            <Option value="已审核">已审核</Option>
            <Option value="已驳回">已驳回</Option>
            <Option value="已完成">已完成</Option>
          </Select>
          <Button type="primary" onClick={handleSearch}>
            搜索
          </Button>
          <Button onClick={handleReset}>重置</Button>
          <Button type="primary" onClick={markEvaluationsAsCompleted}>关闭评价记录公示</Button>
        </Space>
      </Header>

      <Table
        loading={loading}
        components={{
          body: {
            cell: ({ children, ...restProps }) => {
              const { editing, inputNode, ...cellProps } = restProps;
              return (
                <td {...cellProps}>
                  {editing ? (
                    <Form.Item
                      name={restProps.dataIndex}
                      style={{ margin: 0 }}
                      rules={[
                        { required: true, message: `请输入${restProps.title}` },
                      ]}
                    >
                      {inputNode || <Input />}
                    </Form.Item>
                  ) : (
                    children
                  )}
                </td>
              );
            },
          },
        }}
        bordered
        dataSource={data}
        columns={mergedColumns}
        rowClassName="editable-row"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          onChange: (page, pageSize) => {
            setPagination({ current: page, pageSize });
            cancel();
          },
        }}
        scroll={{ y: 400, x: "max-content" }}
      />
    </Form>
  );
};
